/**
 * Team storage utilities for managing profile pictures
 * Uses localStorage to persist team member images across sessions
 */

export interface TeamMember {
  name: string
  position: string
  image: string
  linkedin?: string
  email?: string
}

const TEAM_STORAGE_KEY = 'edicoo-team-images'

/**
 * Save team member images to localStorage
 */
export function saveTeamImages(teamMembers: TeamMember[]): void {
  try {
    const imageMap: Record<string, string> = {}
    teamMembers.forEach(member => {
      if (member.image) {
        imageMap[member.name] = member.image
      }
    })
    localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify(imageMap))
  } catch (error) {
    console.error('Error saving team images:', error)
  }
}

/**
 * Load team member images from localStorage
 */
export function loadTeamImages(): Record<string, string> {
  try {
    const stored = localStorage.getItem(TEAM_STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch (error) {
    console.error('Error loading team images:', error)
    return {}
  }
}

/**
 * Update a single team member's image
 */
export function updateTeamMemberImage(name: string, imageUrl: string): void {
  try {
    const currentImages = loadTeamImages()
    currentImages[name] = imageUrl
    localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify(currentImages))
  } catch (error) {
    console.error('Error updating team member image:', error)
  }
}

/**
 * Get image URL for a team member (falls back to provided default)
 */
export function getTeamMemberImage(name: string, defaultImage: string): string {
  const storedImages = loadTeamImages()
  return storedImages[name] || defaultImage
}

/**
 * Clear all stored team images
 */
export function clearTeamImages(): void {
  try {
    localStorage.removeItem(TEAM_STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing team images:', error)
  }
}
